import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarDismiss } from '@angular/material';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { delay, filter, map, take, takeUntil, tap } from 'rxjs/operators';

export enum NotificationType {
  error = 'error',
  success = 'success'
}

export interface SnackBarQueueItem {
  message: string;
  messageType: NotificationType;
  beingDispatched: boolean;
  configParams?: MatSnackBarConfig;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly queue = new BehaviorSubject<SnackBarQueueItem[]>([]);
  private readonly queue$ = this.queue.asObservable();
  private readonly ngDestroy = new Subject();
  private readonly config: MatSnackBarConfig = {
    duration: 5000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(private snackBar: MatSnackBar) {
    this.queue$
      .pipe(
        filter(queue => queue.length > 0 && !queue[0].beingDispatched),
        tap(() => {
          const updatedQueue = this.queue.value;
          updatedQueue[0].beingDispatched = true;
          this.queue.next(updatedQueue);
        }),
        map(queue => queue[0]),
        takeUntil(this.ngDestroy)
      )
      .subscribe(snackBarItem => this.show(snackBarItem.message, snackBarItem.messageType, snackBarItem.configParams));
  }

  ngOnDestroy() {
    this.queue.next([]);
    this.queue.complete();
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }

  addToQueue(message: string, messageType: NotificationType, configParams?: MatSnackBarConfig) {
    this.queue.next(this.queue.value.concat([{ message, messageType, configParams, beingDispatched: false }]));
  }

  private removeDismissedSnackBar(dismissed: Observable<MatSnackBarDismiss>) {
    dismissed.pipe(delay(300), take(1)).subscribe(() => {
      const updatedQueue = this.queue.value;
      if (updatedQueue[0] && updatedQueue[0].beingDispatched) {
        updatedQueue.shift();
      }
      this.queue.next(updatedQueue);
    });
  }

  show(message: string, type?: NotificationType, config?: MatSnackBarConfig): void {
    this.removeDismissedSnackBar(
      this.snackBar.open(message, 'OK', Object.assign(this.config, config, { panelClass: type })).afterDismissed()
    );
  }
}
